import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Copy, Check, X, Trophy, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { formatCoins } from "../lib/format";

const GAME_NAMES = { aviator: "Aviator", color: "Color Prediction", teenpatti: "Teen Patti", andarbahar: "Andar Bahar", spin: "Lucky Spin", bhole_spin: "Bhole Daily Spin", dice: "Dice", scratch: "Scratch" };

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function WinShareCard({ open, onClose, amount, game, multiplier }) {
  const { t } = useLang();
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/referral").then(({ data }) => setCode(data.code)).catch(() => {});
  }, [open]);

  const link = code ? `${window.location.origin}/login?ref=${code}` : window.location.origin;
  const gname = GAME_NAMES[game] || game;
  const text = `🎉 मैंने Bam Bam Bhole पर ${gname} में ${formatCoins(amount)} जीते! तुम भी खेलो 👉`;
  const full = `${text} ${link}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success(t("copied")); } catch {}
  };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Bam Bam Bhole", text, url: link }); } catch {} }
    else copy();
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(full)}`, "_blank");

  const makeImage = () => new Promise((resolve) => {
    const logo = new Image();
    logo.onload = () => draw(logo);
    logo.onerror = () => draw(null);
    logo.src = "/brand_logo.jpg";
    const draw = (img) => {
    const W = 720, H = 900;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#13205a"); bg.addColorStop(1, "#050816");
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
    const rg = ctx.createRadialGradient(W / 2, 200, 30, W / 2, 200, 460);
    rg.addColorStop(0, "rgba(255,138,31,0.45)"); rg.addColorStop(1, "rgba(255,138,31,0)");
    ctx.fillStyle = rg; ctx.fillRect(0, 0, W, 420);
    ctx.strokeStyle = "rgba(255,215,0,0.55)"; ctx.lineWidth = 6; roundRect(ctx, 24, 24, W - 48, H - 48, 28); ctx.stroke();
    ctx.textAlign = "center";
    ctx.fillStyle = "#FFD700"; ctx.font = "bold 48px sans-serif"; ctx.fillText("BAM BAM BHOLE", W / 2, 105);
    if (img) { ctx.save(); roundRect(ctx, W / 2 - 95, 150, 190, 190, 28); ctx.clip(); ctx.drawImage(img, W / 2 - 95, 150, 190, 190); ctx.restore(); }
    ctx.fillStyle = "#FFB566"; ctx.font = "bold 32px sans-serif";
    ctx.fillText(gname + (multiplier ? ` · ${multiplier.toFixed(2)}×` : ""), W / 2, 400);
    ctx.fillStyle = "#FFD700"; ctx.font = "bold 112px sans-serif"; ctx.fillText("+" + formatCoins(amount), W / 2, 520);
    ctx.fillStyle = "#cbd5e1"; ctx.font = "27px sans-serif"; ctx.fillText(t("shareWinTitle"), W / 2, 580);
    ctx.fillStyle = "rgba(0,0,0,0.45)"; roundRect(ctx, 70, 650, W - 140, 110, 18); ctx.fill();
    ctx.fillStyle = "#94a3b8"; ctx.font = "22px sans-serif"; ctx.fillText("Join & play 👇", W / 2, 696);
    ctx.fillStyle = "#FFD700"; ctx.font = "bold 24px sans-serif"; ctx.fillText(link, W / 2, 732);
    c.toBlob((b) => resolve(b), "image/png");
    };
  });

  const download = async () => {
    setSaving(true);
    try {
      const blob = await makeImage();
      const file = new File([blob], "bambambhole-win.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text }); return; } catch {}
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "bambambhole-win.png"; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("imageSaved"));
    } catch { toast.error("Could not create image"); }
    finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} className="fixed inset-0 z-[60] grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="win-share-modal">
          <motion.div initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()} className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-amber-400/40"
            style={{ background: "radial-gradient(circle at 50% 0%, rgba(157,78,221,0.35), #0b0614 70%)" }}>
            <button onClick={onClose} data-testid="win-share-close" className="absolute right-3 top-3 text-slate-400 hover:text-white"><X size={18} /></button>
            <div className="p-7 text-center">
              <div className="mx-auto mb-3 grid h-16 w-16 place-items-center rounded-full bg-amber-400/15 border border-amber-400/40" style={{ filter: "drop-shadow(0 0 16px rgba(255,215,0,0.5))" }}>
                <Trophy size={30} className="text-amber-300" />
              </div>
              <p className="text-xs uppercase tracking-widest text-cyan-300">{gname}{multiplier ? ` · ${multiplier.toFixed(2)}×` : ""}</p>
              <p className="mt-1 font-display text-4xl font-extrabold gold-text" data-testid="win-share-amount">+{formatCoins(amount)}</p>
              <p className="mt-1 text-sm text-slate-300">{t("shareWinTitle")}</p>

              <div className="mt-5 flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2">
                <span className="truncate text-xs text-slate-400">{link}</span>
                <button onClick={copy} data-testid="win-share-copy" className="ml-auto shrink-0 text-amber-300">{copied ? <Check size={16} /> : <Copy size={16} />}</button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button onClick={whatsapp} data-testid="win-share-whatsapp" className="rounded-xl bg-emerald-500 py-2.5 text-sm font-bold text-black hover:bg-emerald-400 transition-colors">WhatsApp</button>
                <button onClick={share} data-testid="win-share-share" className="rounded-xl btn-gold py-2.5 text-sm flex items-center justify-center gap-1.5"><Share2 size={15} /> {t("shareWin")}</button>
              </div>
              <button onClick={download} disabled={saving} data-testid="win-share-download"
                className="mt-2 w-full rounded-xl border border-cyan-400/40 bg-cyan-500/10 py-2.5 text-sm font-semibold text-cyan-200 hover:border-cyan-400 transition-colors flex items-center justify-center gap-1.5">
                <Download size={15} /> {t("downloadImage")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ShareWin({ amount, game, multiplier }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="share-win-btn"
        className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 hover:border-cyan-400 transition-colors">
        <Share2 size={13} /> {t("shareWin")}
      </button>
      <WinShareCard open={open} onClose={() => setOpen(false)} amount={amount} game={game} multiplier={multiplier} />
    </>
  );
}
